import { Link } from "react-router-dom";
import { ArrowRight, Code, Sparkles, Wrench, Webhook, Cpu, Layers, GitBranch, ShieldCheck } from "lucide-react";

const TRACKS = [
  {
    icon: Layers,
    tag: "Track 01",
    title: "Core APIs",
    desc: "Quotes · Cities · Exchange · Social · Translation — all public REST. Build apps on top of every Available primitive.",
  },
  {
    icon: Code,
    tag: "Track 02",
    title: "City-Page Generators",
    desc: "Tap into 23K+ seeded city slugs. Build vertical micro-sites that piggyback on the Available footprint.",
  },
  {
    icon: Webhook,
    tag: "Track 03",
    title: "Exchange Hooks",
    desc: "Subscribe to new listings + closed-deal events. Sync into your CRM, DealRoom, or feed a private terminal.",
  },
  {
    icon: Cpu,
    tag: "Track 04",
    title: "AvailableMe Embeds",
    desc: "Render any post on any site. Build players, dashboards, and creator overlays.",
  },
  {
    icon: Wrench,
    tag: "Track 05",
    title: "Translation Pipelines",
    desc: "Pipe content through our Claude-powered translation hub. CRM-ready. Multi-tenant.",
  },
  {
    icon: Sparkles,
    tag: "Track 06",
    title: "Intel Streams",
    desc: "Business Opportunities, Market Predictions, Political Outcomes, World Events — as a feed.",
  },
];

const ENDPOINTS = [
  { method: "GET",  path: "/api/services",                desc: "All 10 removal divisions" },
  { method: "GET",  path: "/api/cities/search?q=…",       desc: "Search 23K+ cities" },
  { method: "GET",  path: "/api/cities/by-state/{code}",  desc: "All cities in a US state / CA province" },
  { method: "GET",  path: "/api/exchange/listings",       desc: "Active B2B deals with filters" },
  { method: "POST", path: "/api/exchange/listings",       desc: "Post a deal ($1M floor)" },
  { method: "GET",  path: "/api/exchange/closed-deals",   desc: "The public Closed Deals Wall" },
  { method: "POST", path: "/api/exchange/honor-pledge",   desc: "Record an honour pledge" },
  { method: "GET",  path: "/api/social/feed",             desc: "AvailableMe interactive posts" },
  { method: "POST", path: "/api/social/post",             desc: "Compose an interactive post" },
  { method: "POST", path: "/api/translate",               desc: "Claude-powered translation" },
  { method: "GET",  path: "/api/intel",                   desc: "Radical Intel buckets" },
  { method: "GET",  path: "/api/market/ticker",           desc: "Live market ticker" },
  { method: "GET",  path: "/api/countries",               desc: "Worldwide markets" },
  { method: "GET",  path: "/api/languages",               desc: "Supported languages" },
];

export default function Developers() {
  return (
    <>
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="dev-hero">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ DEVELOPERS · BUILD ON AVAILABLE
          </div>
          <h1 className="display-black text-5xl lg:text-7xl uppercase mt-6 leading-[0.95] tracking-tighter max-w-5xl">
            Every Available primitive,<br /><span className="text-[#FACC15]">as an API.</span>
          </h1>
          <p className="text-lg text-white/85 mt-6 max-w-3xl">
            We're building the operating system for the disposal, removal, and asset-transfer economy. Anything we publish becomes a primitive other builders can stack on. Wire in. Build something we didn't think of.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <a href="#endpoints" className="btn-signal" data-testid="dev-endpoints-cta">View Endpoints <ArrowRight className="w-4 h-4" /></a>
            <Link to="/consulting" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }} data-testid="dev-partner-cta">Partner with Us</Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Tracks</div>
          <h2 className="display-black text-3xl lg:text-4xl uppercase text-[#064E3B] mt-2">Six places to plug in.</h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-300 border border-gray-300">
            {TRACKS.map(t => {
              const Icon = t.icon;
              return (
                <div key={t.title} className="bento-card p-7" data-testid={`dev-track-${t.tag.toLowerCase().replace(/\s/g, "-")}`}>
                  <Icon className="w-7 h-7 text-[#D97706]" strokeWidth={2} />
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-gray-500 mt-3">// {t.tag}</div>
                  <h3 className="display-black text-xl uppercase text-[#064E3B] mt-2">{t.title}</h3>
                  <p className="text-sm text-gray-700 mt-3">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50" id="endpoints">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Public Endpoints</div>
          <h2 className="display-black text-3xl lg:text-4xl uppercase text-[#064E3B] mt-2">Read-only is free. Forever.</h2>
          <p className="text-gray-700 mt-3 max-w-2xl">No rate limits announced yet — be reasonable. Premium tier (real-time, webhooks, batch) coming soon.</p>

          <div className="mt-10 border border-gray-300 bg-white divide-y divide-gray-200" data-testid="dev-endpoints">
            {ENDPOINTS.map(e => (
              <div key={e.path} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-4 hover:bg-gray-50 transition-colors">
                <div className="md:col-span-1">
                  <span className={`mono text-[10px] tracking-wider uppercase px-2 py-0.5 ${e.method === "GET" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{e.method}</span>
                </div>
                <code className="md:col-span-5 mono text-[12px] text-[#064E3B] break-all">{e.path}</code>
                <div className="md:col-span-6 text-sm text-gray-700">{e.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="border border-[#064E3B] bg-[#064E3B] text-white p-8">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-[#FACC15]" />
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#FACC15]">// Builders Program</div>
            </div>
            <h3 className="display-black text-2xl uppercase mt-3">Sneaky launch. Explosive growth.</h3>
            <p className="text-white/80 mt-3">We seed builders with API access, sandbox data, and direct lines to the core team. If your app proves utility, we co-promote.</p>
            <Link to="/join/sales-team" className="btn-ghost mt-6" style={{ color: "#FACC15", borderColor: "#FACC15" }} data-testid="dev-apply-builder">
              Apply via Power Sales <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="border border-gray-300 p-8">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#064E3B]" />
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Trust & Ethics</div>
            </div>
            <h3 className="display-black text-2xl uppercase text-[#064E3B] mt-3">We're slow on purpose.</h3>
            <p className="text-gray-800 mt-3">We're building toward a global infrastructure for asset transfer. We won't ship features that compromise honour, verification or the operator-grade quality of the network. If you want to surpass the big platforms — build with us, not against us.</p>
          </div>
        </div>
      </section>
    </>
  );
}
